import { create } from 'zustand';
import type { CognitoUser } from 'amazon-cognito-identity-js';
import {
  getCurrentSession,
  signIn as apiSignIn,
  signInWithWebAuthn as apiSignInWithWebAuthn,
  respondToMfaChallenge as apiRespondToMfa,
  respondToWebAuthnChallenge as apiRespondToWebAuthn,
  signUp as apiSignUp,
  confirmSignUp as apiConfirmSignUp,
  signOut as apiSignOut,
} from '@/api/auth';

export interface AuthUser {
  userId: string;
  email: string;
  name: string;
}

export interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  needsConfirmation: boolean;
  needsMfaCode: boolean;
  mfaCognitoUser: CognitoUser | null;
  needsWebAuthn: boolean;
  webAuthnChallengeOptions: string | null;
  webAuthnCognitoUser: CognitoUser | null;
  pendingEmail: string;
  /** @internal password kept temporarily for TOTP→WebAuthn switch */
  _pendingPassword: string | null;
  error: string | null;

  checkSession: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  submitMfaCode: (code: string) => Promise<void>;
  switchToWebAuthn: () => Promise<void>;
  submitWebAuthn: () => Promise<void>;
  signup: (email: string, password: string, name: string) => Promise<void>;
  confirmCode: (email: string, code: string) => Promise<void>;
  logout: () => void;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  needsConfirmation: false,
  needsMfaCode: false,
  mfaCognitoUser: null,
  needsWebAuthn: false,
  webAuthnChallengeOptions: null,
  webAuthnCognitoUser: null,
  pendingEmail: '',
  _pendingPassword: null,
  error: null,

  checkSession: async () => {
    try {
      const session = await getCurrentSession();
      if (session) {
        set({
          user: {
            userId: session.userId,
            email: session.email,
            name: session.email,
          },
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });
      } else {
        set({ user: null, isAuthenticated: false, isLoading: false });
      }
    } catch {
      set({ user: null, isAuthenticated: false, isLoading: false });
    }
  },

  login: async (email: string, password: string) => {
    set({ isLoading: true, error: null, needsMfaCode: false, mfaCognitoUser: null, needsWebAuthn: false, webAuthnCognitoUser: null, webAuthnChallengeOptions: null });
    try {
      const result = await apiSignIn(email, password);
      if (result.type === 'mfa') {
        set({
          isLoading: false,
          needsMfaCode: true,
          mfaCognitoUser: result.cognitoUser,
          pendingEmail: email,
          _pendingPassword: password,
        });
      } else if (result.type === 'webauthn') {
        set({
          isLoading: false,
          needsWebAuthn: true,
          webAuthnChallengeOptions: result.challengeOptions,
          webAuthnCognitoUser: result.cognitoUser,
          pendingEmail: email,
        });
      } else {
        set({
          user: {
            userId: result.session.userId,
            email: result.session.email,
            name: result.session.email,
          },
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });
      }
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Sign in failed';
      set({ isLoading: false, error: message });
    }
  },

  submitMfaCode: async (code: string) => {
    const cognitoUser = get().mfaCognitoUser;
    if (!cognitoUser) {
      set({ error: 'No MFA session in progress' });
      return;
    }
    set({ isLoading: true, error: null });
    try {
      const session = await apiRespondToMfa(cognitoUser, code);
      set({
        user: {
          userId: session.userId,
          email: session.email,
          name: session.email,
        },
        isAuthenticated: true,
        isLoading: false,
        needsMfaCode: false,
        mfaCognitoUser: null,
        error: null,
      });
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Invalid MFA code';
      set({ isLoading: false, error: message });
    }
  },

  switchToWebAuthn: async () => {
    const email = get().pendingEmail;
    const password = get()._pendingPassword;
    if (!email || !password) {
      set({ error: 'Please sign in again to use your security key' });
      return;
    }
    set({ isLoading: true, error: null, needsMfaCode: false, mfaCognitoUser: null });
    try {
      const session = await apiSignInWithWebAuthn(email, password);
      set({
        user: { userId: session.userId, email: session.email, name: session.email },
        isAuthenticated: true,
        isLoading: false,
        needsMfaCode: false,
        _pendingPassword: null,
        error: null,
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to switch to security key';
      set({ isLoading: false, error: message, needsMfaCode: true });
    }
  },

  submitWebAuthn: async () => {
    const { webAuthnCognitoUser: cognitoUser, webAuthnChallengeOptions: options } = get();
    if (!cognitoUser || !options) {
      set({ error: 'No WebAuthn session in progress' });
      return;
    }
    set({ isLoading: true, error: null });
    try {
      const { startAuthentication } = await import('@simplewebauthn/browser');
      const parsedOptions = JSON.parse(options) as Parameters<typeof startAuthentication>[0]['optionsJSON'];
      const assertion = await startAuthentication({ optionsJSON: parsedOptions });
      const session = await apiRespondToWebAuthn(cognitoUser, JSON.stringify(assertion));
      set({
        user: {
          userId: session.userId,
          email: session.email,
          name: session.email,
        },
        isAuthenticated: true,
        isLoading: false,
        needsWebAuthn: false,
        webAuthnChallengeOptions: null,
        webAuthnCognitoUser: null,
        error: null,
      });
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'WebAuthn verification failed';
      set({ isLoading: false, error: message });
    }
  },

  signup: async (email: string, password: string, name: string) => {
    set({ isLoading: true, error: null });
    try {
      await apiSignUp(email, password, name);
      set({
        isLoading: false,
        needsConfirmation: true,
        pendingEmail: email,
        error: null,
      });
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Sign up failed';
      set({ isLoading: false, error: message });
    }
  },

  confirmCode: async (email: string, code: string) => {
    set({ isLoading: true, error: null });
    try {
      await apiConfirmSignUp(email, code);
      set({
        isLoading: false,
        needsConfirmation: false,
        pendingEmail: '',
        error: null,
      });
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Confirmation failed';
      set({ isLoading: false, error: message });
    }
  },

  logout: () => {
    apiSignOut();
    set({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      needsConfirmation: false,
      needsMfaCode: false,
      mfaCognitoUser: null,
      needsWebAuthn: false,
      webAuthnChallengeOptions: null,
      webAuthnCognitoUser: null,
      pendingEmail: '',
      _pendingPassword: null,
      error: null,
    });
  },

  clearError: () => set({ error: null }),
}));
